import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import Dashboard from './pages/Dashboard';
import RecommendationDetail from './pages/RecommendationDetail';
import History from './pages/History';
import Positions from './pages/Positions';
import Signal from './pages/Signal';
import Options from './pages/Options';
import { BarChart3, History as HistoryIcon, Briefcase, LayoutDashboard, Radio, Menu, X, Layers } from 'lucide-react';
import './App.css';

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/signal', label: 'Signal', icon: Radio },
  { to: '/options', label: 'Options', icon: Layers },
  { to: '/positions', label: 'Positions', icon: Briefcase },
  { to: '/history', label: 'History', icon: HistoryIcon },
];

function NavContent() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/80 border-b border-[hsl(var(--border))]">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 md:w-7 md:h-7 text-[hsl(var(--primary))]" />
            <h1 className="text-lg md:text-xl font-bold tracking-tight">
              <span className="text-[hsl(var(--primary))]">Trade</span>Pulse
              <span className="text-xs ml-1 text-[hsl(var(--muted-foreground))] font-medium">AI</span>
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-1" data-testid="main-navigation">
            {navItems.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({isActive}) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive ? 'bg-[hsl(var(--accent))] text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]'
                }`} data-testid={`nav-${item.label.toLowerCase()}`}>
                <item.icon className="w-4 h-4" />{item.label}
              </NavLink>
            ))}
          </div>
          <button className="md:hidden p-2 rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-button">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-[hsl(var(--border))] bg-[hsl(var(--background))] animate-fade-in">
            <div className="px-4 py-3 space-y-1">
              {navItems.map(item => (
                <NavLink key={item.to} to={item.to} end={item.end}
                  className={({isActive}) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    isActive ? 'bg-[hsl(var(--accent))] text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))]'
                  }`}>
                  <item.icon className="w-5 h-5" />{item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[hsl(var(--background))]/95 backdrop-blur border-t border-[hsl(var(--border))] safe-area-bottom">
        <div className="flex items-center justify-around py-2 px-1">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({isActive}) => `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors duration-150 ${
                isActive ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--muted-foreground))]'
              }`} data-testid={`tab-${item.label.toLowerCase()}`}>
              <item.icon className="w-5 h-5" />{item.label}
            </NavLink>
          ))}
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
        <NavContent />
        <main className="mx-auto max-w-[1440px] px-4 md:px-8 py-4 md:py-6 pb-24 md:pb-6">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/signal" element={<Signal />} />
            <Route path="/options" element={<Options />} />
            <Route path="/recommendation/:id" element={<RecommendationDetail />} />
            <Route path="/history" element={<History />} />
            <Route path="/positions" element={<Positions />} />
          </Routes>
        </main>
        <Toaster position="top-right" richColors />
      </div>
    </Router>
  );
}

export default App;
